import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import callApi from 'utils/api';
import takeLeading from 'utils/effect-creators';
import { fetchPublishAllCount } from '../../gantt/containers/gantt-actions';
import * as actions from './publish-steps-actions';
import * as types from './publish-steps-constants';

export function* unlockLock(action) {
  try {
    const { url, id, reloadFn, isPlanningBoard, boardId } = action.payload;
    const results = yield call(callApi, {
      endpoint: url,
      options: {
        method: 'POST',
        errorMessage: `Could not unlock lock from ${id}`,
        retryAction: action,
      },
    });
    if (isPlanningBoard) {
      yield put(fetchPublishAllCount({ boardId }));
    }
    yield put(reloadFn(results));
    yield put(actions.unlockLockSuccess(results));
  } catch (err) {
    yield put(actions.unlockLockFailure(err));
  }
}

export function* discardLock(action) {
  try {
    const { url, id, reloadFn, isPlanningBoard, boardId } = action.payload;
    const results = yield call(callApi, {
      endpoint: url,
      options: {
        method: 'POST',
        errorMessage: `Could not discard lock from ${id}`,
        retryAction: action,
      },
    });
    if (isPlanningBoard) {
      yield put(fetchPublishAllCount({ boardId }));
    }
    yield put(reloadFn(results));
    yield put(actions.discardLockSuccess(results));
  } catch (err) {
    yield put(actions.discardLockFailure(err));
  }
}

export function* fetchCurrentStatus(action) {
  try {
    const { id, type, allowUnknown = false } = action.payload;
    const results = yield call(callApi, {
      endpoint: `/status/${type}/${id}`,
      options: {
        errorMessage: `Could not fetch the current status from ${id}`,
        retryAction: action,
      },
    });
    yield put(actions.currentStatusSuccess({ ...results, allowUnknown }));
  } catch (err) {
    yield put(actions.currentStatusFailure(err));
  }
}

export function* doAbort(action) {
  try {
    const { id, type, statusCode } = action.payload;
    const body = {
      statusCode,
    };
    const results = yield call(callApi, {
      endpoint: `/status/${type}/${id}/abort`,
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        errorMessage: 'Could not abort',
        retryAction: action,
      },
    });
    yield put(actions.abortSuccess(results));
  } catch (err) {
    yield put(actions.abortFailure(err));
  }
}

export function* fetchMultiStatus(action) {
  try {
    const { ids } = action.payload;
    const body = {
      ids,
    };
    const results = yield call(callApi, {
      endpoint: '/status',
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        errorMessage: 'Could not fetch multiple statusses',
        retryAction: action,
      },
    });
    yield put(actions.multiStatusSuccess(results));
  } catch (err) {
    yield put(actions.multiStatusFailure(err));
  }
}

export function* backgroundPolling(action) {
  const { id, type, allowUnknown = false } = action.payload;
  while (yield select(state => state.currentStatus.shouldPoll)) {
    const interval = yield select(state => state.currentStatus.operationInfo.pollingInterval);
    yield put(actions.currentStatusRequest({ id, type, allowUnknown }));
    yield delay(interval * 1000 || 10000);
  }
}

export function* backgroundMultiPolling() {
  while (
    yield select(state => state.currentStatus.shouldMultiPoll)
  ) {
    const ids = yield select(state => state.currentStatus.idsToPoll);
    const interval = yield select(state => state.currentStatus.multiPollingInterval);
    yield put(actions.multiStatusRequest({ ids }));
    yield delay(interval * 1000 || 10000);
  }
}

export function* pollCurrentStatus(action) {
  const task = yield fork(() => backgroundPolling(action));
  yield take(types.STOP_PUBLISH_STEP_POLL);
  yield cancel(task);
}

export function* pollMultiStatus() {
  const task = yield fork(() => backgroundMultiPolling());
  yield take(types.STOP_MULTI_STATUS_POLL);
  yield cancel(task);
}

function* watchAbort() {
  yield takeLatest(types.ABORT_REQUEST, doAbort);
}

function* watchStartPolling() {
  yield takeLatest(types.START_PUBLISH_STEP_POLL, pollCurrentStatus);
}

function* watchUnlockLock() {
  yield takeLatest(types.UNLOCK_LOCK_REQUEST, unlockLock);
}

function* watchDiscardLock() {
  yield takeLatest(types.DISCARD_LOCK_REQUEST, discardLock);
}

function* watchFetchCurrentStatus() {
  yield takeLeading(types.CURRENT_STATUS_REQUEST, fetchCurrentStatus);
}

function* watchFetchMultiStatus() {
  yield takeLeading(types.MULTI_STATUS_REQUEST, fetchMultiStatus);
}

function* watchFetchMultiStatusFromSockets() {
  yield takeEvery(types.MULTI_STATUS_SOCKET_REQUEST, fetchMultiStatus);
}

function* watchStartMultiPolling() {
  yield takeLatest(types.START_MULTI_STATUS_POLL, pollMultiStatus);
}

export default [
  fork(watchUnlockLock),
  fork(watchDiscardLock),
  fork(watchStartPolling),
  fork(watchFetchCurrentStatus),
  fork(watchFetchMultiStatus),
  fork(watchStartMultiPolling),
  fork(watchAbort),
  fork(watchFetchMultiStatusFromSockets),
];
